import { css } from "@emotion/css";
import type { PrimaryPageAction, SimpleMenuItem } from "@octopusdeploy/design-system-components";
import { Badge, noOp, EmptyStateOnboarding, EmptyStatePlatformHubPoliciesIllustration, Pagination } from "@octopusdeploy/design-system-components";
import { ExperimentIcon } from "@octopusdeploy/design-system-icons";
import { space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { GetPoliciesBffResponse, PlatformHubConnectionConfigurationResource, PolicySummary, Repository, ValidateGitRefV2Response } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import cn from "classnames";
import type { ReactElement } from "react";
import * as React from "react";
import { lastAccessedPlatformHubGitRef } from "~/areas/platformHub/LastAccessedPlatformHubGitRef";
import { PlatformHubPageHeaderBranchSelector } from "~/areas/platformHub/PlatformHubPageHeaderBranchSelector";
import { usePlatformHubGitRef } from "~/areas/platformHub/usePlatformHubGitRef";
import { client } from "~/clientInstance";
import FilterSearchBox from "~/components/FilterSearchBox/index";
import ExternalLink from "~/components/Navigation/ExternalLink/index";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import WarningIcon from "~/components/WarningIcon/index";
import { getValueInStorage } from "~/hooks/localStorageHelpers";
import useLocalStorage from "~/hooks/useLocalStorage";
import { DataTable, DataTableBody, DataTableHeader, DataTableHeaderColumn, DataTableRow, DataTableRowColumn } from "~/primitiveComponents/dataDisplay/DataTable/index";
interface PoliciesPageProps {
    loaderData: LoaderData;
    queryParams: PoliciesPageQueryParams;
    setQueryParams: QueryParamValuesSetter<PoliciesPageQueryParams>;
}
interface LoaderData {
    policiesResponse: GetPoliciesBffResponse;
    platformHubConnectionConfiguration: PlatformHubConnectionConfigurationResource;
    initialValidationResult: ValidateGitRefV2Response | undefined;
}
export interface PoliciesPageQueryParams {
    searchValue: string;
    page: number | undefined;
    pageSize: number | undefined;
    gitRef: string;
}
const defaultPageSize = 30;
const emptyPoliciesResponse: GetPoliciesBffResponse = {
    Policies: [],
    FilteredItemsCount: 0,
    ItemsPerPage: 0,
    TotalItemsCount: 0,
};
export async function policiesPageLoader(repository: Repository, queryParams: PoliciesPageQueryParams): Promise<LoaderData> {
    const platformHubConnectionConfiguration = await repository.PlatformHubConnection.get();
    if (!platformHubConnectionConfiguration.PersistenceSettings.Url) {
        return {
            policiesResponse: emptyPoliciesResponse,
            platformHubConnectionConfiguration,
            initialValidationResult: undefined,
        };
    }
    let gitRef = queryParams.gitRef;
    if (!gitRef) {
        gitRef = lastAccessedPlatformHubGitRef.get(platformHubConnectionConfiguration.PersistenceSettings);
    }
    const initialValidationResult = await repository.PlatformHub.validateGitRef(gitRef);
    const userPageSize = getValueInStorage(`Octopus.PoliciesList.PageSize`);
    const pageSize = userPageSize ? parseInt(userPageSize) : defaultPageSize;
    const policiesResponse = await repository.Policy.getPaginatedPolicies({
        skip: ((queryParams.page ?? 1) - 1) * (queryParams.pageSize ?? pageSize),
        take: queryParams.pageSize ?? pageSize,
        partialName: queryParams.searchValue,
        gitRef: initialValidationResult.GitRef?.CanonicalName ?? gitRef,
    });
    return {
        policiesResponse,
        platformHubConnectionConfiguration,
        initialValidationResult,
    };
}
export function PoliciesPage({ loaderData, queryParams, setQueryParams }: PoliciesPageProps) {
    const [gitRef, setGitRef] = usePlatformHubGitRef(loaderData.platformHubConnectionConfiguration.PersistenceSettings, queryParams, setQueryParams, loaderData.initialValidationResult);
    const [userDefaultPageSize, setUserDefaultPageSize] = useLocalStorage(`Octopus.PoliciesList.PageSize`, defaultPageSize);
    const { result: pagingData, refetch: reload } = useQuery((repository) => {
        if (!gitRef) {
            return Promise.resolve(emptyPoliciesResponse);
        }
        return repository.Policy.getPaginatedPolicies({
            skip: ((queryParams.page ?? 1) - 1) * (queryParams.pageSize ?? userDefaultPageSize),
            take: queryParams.pageSize ?? userDefaultPageSize,
            partialName: queryParams.searchValue,
            gitRef,
        });
    }, [gitRef, queryParams.page, queryParams.pageSize, queryParams.searchValue, userDefaultPageSize], "Get paginated policies", {
        initialResult: loaderData.policiesResponse,
    });
    const onPageChange = (page: number) => {
        setQueryParams({ ...queryParams, page });
    };
    const onPageSizeChange = (pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
        setUserDefaultPageSize(pageSize);
    };
    const onSearch = (value: string) => {
        setQueryParams({ ...queryParams, searchValue: value, page: 1 });
    };
    if (!gitRef || !loaderData.platformHubConnectionConfiguration.PersistenceSettings.Url) {
        return (<PageContent header={getPoliciesHeader()}>
                <EmptyPoliciesOnboarding actionString={"To add a policy, you\u2019ll need to set up a Git connection."} primaryAction={{
                type: "navigate",
                label: "Configure Version Control",
                hasPermissions: isAllowed({ permission: Permission.PlatformHubEdit }),
                onClick: noOp,
                path: links.platformHubVersionControlSettingsPage.generateUrl(),
            }}/>
            </PageContent>);
    }
    const showOnboarding = pagingData.TotalItemsCount === 0;
    const titleAccessory = <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} gitPersistenceSettings={loaderData.platformHubConnectionConfiguration.PersistenceSettings}/>;
    const overflowActions: SimpleMenuItem[] = [];
    if (isAllowed({ permission: Permission.EventView, wildcard: true })) {
        overflowActions.push({
            type: "button",
            label: "Download audit log",
            onClick: () => {
                window.location.href = client.resolveLinkTemplate("Events", { eventCategories: ["CompliancePolicyEvaluated"], asCsv: true });
            },
        });
    }
    if (showOnboarding) {
        return (<PageContent header={getPoliciesHeader(titleAccessory)}>
                <EmptyPoliciesOnboarding actionString={"To start, create a policy in your linked Git repository."}/>
            </PageContent>);
    }
    return (<PageContent header={getPoliciesHeader(titleAccessory)} filters={{
            inputs: [<FilterSearchBox value={queryParams.searchValue} onChange={onSearch} placeholder={"Filter by name..."} autoFocus={false} debounceDelay={500}/>],
        }} pagination={{
            ui: (<Pagination label="Policies" totalResults={pagingData.FilteredItemsCount} itemsPerPageOptions={[30, 50, 100]} selectedItemsPerPage={pagingData.ItemsPerPage} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
            placement: "bottom",
        }}>
            <DataTable>
                <DataTableHeader>
                    <DataTableRow>
                        <DataTableHeaderColumn>Name</DataTableHeaderColumn>
                        <DataTableHeaderColumn className={cn(styles.descriptionColumn)}>Description</DataTableHeaderColumn>
                        <DataTableHeaderColumn>File</DataTableHeaderColumn>
                    </DataTableRow>
                </DataTableHeader>
                <DataTableBody>
                    {pagingData.Policies.map((p) => (<PolicyRow policy={p} key={p.Id}/>))}
                </DataTableBody>
            </DataTable>
        </PageContent>);
}
const EmptyPoliciesOnboarding = ({ actionString, primaryAction }: {
    actionString: string;
    primaryAction?: PrimaryPageAction;
}) => (<EmptyStateOnboarding title="Enforce deployment policies" description={<>
                <p>Ensure that your teams are compliant and secure by default. You can craft and manage deployment rules that can be applied across projects and spaces.</p>
                <p>{actionString}</p>
            </>} image={<EmptyStatePlatformHubPoliciesIllustration />} primaryAction={primaryAction} learnMore={<ExternalLink href="policies">Docs: Policies</ExternalLink>}/>);
const getPoliciesHeader = (contextSelector?: ReactElement) => {
    return { title: "Policies", contextSelector, chip: <Badge variant={"feature"} size={"medium"} label={"Alpha"} icon={<ExperimentIcon size={20} color={themeTokens.color.badge.icon.feature}/>}/> };
};
interface PolicyRowProps {
    policy: PolicySummary;
}
function PolicyRow({ policy }: PolicyRowProps) {
    const file = `${policy.Slug}.ocl`;
    return (<DataTableRow className={styles.middle}>
            <DataTableRowColumn className={styles.middle}>
                <div className={styles.name}>{policy.Name}</div>
            </DataTableRowColumn>
            <DataTableRowColumn className={styles.middle}>
                <DescriptionOrWarning policy={policy}/>
            </DataTableRowColumn>
            <DataTableRowColumn className={styles.middle}>
                <code>{file}</code>
            </DataTableRowColumn>
        </DataTableRow>);
}
function DescriptionOrWarning({ policy }: PolicyRowProps) {
    if (policy.Warning) {
        const warning = policy.Warning;
        const message = warning.Details ? `${warning.Message}: ${warning.Details}` : warning.Message;
        return (<div className={styles.description}>
                <WarningIcon />
                <div>{message}</div>
            </div>);
    }
    return <div className={styles.description}>{policy.Description}</div>;
}
const styles = {
    rowCellPadding: css({ paddingLeft: space[1] }),
    middle: css({
        verticalAlign: "middle !important",
    }),
    right: css({
        verticalAlign: "middle !important",
        float: "right",
    }),
    nameContainer: css({ display: "flex", alignItems: "center", gap: space[12] }),
    name: css({ paddingLeft: space[6] }),
    descriptionColumn: css({
        width: "40%",
    }),
    description: css({
        paddingLeft: space[1],
        display: "flex",
        alignItems: "center",
        gap: space[4],
    }),
};
